import { Alert } from "@/components/ui/alert";
import {
  CardLink,
  type CardLinkProps,
} from "@/components/primitives/card-link";
import { cn } from "@/lib/utils";
import { ArrowRightIcon } from "lucide-react";
import type { ComponentPropsWithoutRef, HTMLAttributes } from "react";

export function ArticleContainer({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("mx-auto max-w-3xl", className)}>{children}</div>;
}

export function Article({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <article
      {...props}
      className={cn(
        "prose prose-base prose-headings:scroll-mt-24 prose-headings:font-semibold prose-h2:mt-10 prose-h2:mb-4 prose-p:leading-relaxed prose-li:leading-relaxed max-w-none",
        className,
      )}
    />
  );
}

export function ArticleHeader({
  className,
  ...props
}: ComponentPropsWithoutRef<"header">) {
  return <header {...props} className={cn("mb-8 space-y-2", className)} />;
}

export function ArticleBody({
  className,
  ...props
}: ComponentPropsWithoutRef<"div">) {
  return <div {...props} className={cn("mb-8 space-y-8", className)} />;
}

export function ArticleHeading({
  className,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return <h1 {...props} className={cn("text-4xl font-bold", className)} />;
}

export function ArticleDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      {...props}
      className={cn("text-muted-foreground text-base", className)}
    />
  );
}

export function ArticleSection({
  className,
  ...props
}: HTMLAttributes<HTMLElement>) {
  return <section {...props} className={cn("space-y-4", className)} />;
}

interface ArticleSectionHeaderProps extends Omit<
  HTMLAttributes<HTMLHeadingElement>,
  "id"
> {
  id: string;
}

export function ArticleSectionHeader({
  className,
  children,
  id,
  ...props
}: ArticleSectionHeaderProps) {
  return (
    <h2
      {...props}
      id={id}
      className={cn(
        "group mt-12 mb-8 text-2xl leading-10 font-semibold",
        className,
      )}
    >
      <a href={`#${id}`} className="relative -ml-6 block pl-6 no-underline">
        <span
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-0 -translate-y-1/2 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        >
          #
        </span>
        <span className="decoration-primary underline [text-decoration-style:wavy] decoration-2 underline-offset-8">
          {children}
        </span>
      </a>
    </h2>
  );
}

export function ArticleSectionSubHeader({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      {...props}
      className={cn(
        "group mt-6 mb-4 text-xl leading-8 font-semibold",
        className,
      )}
    >
      {children}
    </h3>
  );
}

export function ArticleParagraph({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p {...props} className={cn("text-lg", className)} />;
}

export function ArticleList({
  className,
  ...props
}: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      {...props}
      className={cn("list-none space-y-2 text-lg md:pl-3", className)}
    />
  );
}

export function ArticleListItem({
  className,
  ...props
}: HTMLAttributes<HTMLLIElement>) {
  return (
    <li
      {...props}
      className={cn(
        "before:text-primary relative pl-7 before:absolute before:top-[0.1em] before:left-0 before:text-base before:content-['✦']",
        className,
      )}
    />
  );
}

export function ArticleOrderedList({
  className,
  ...props
}: HTMLAttributes<HTMLOListElement>) {
  return (
    <ol
      {...props}
      className={cn(
        "list-outside list-decimal space-y-2 pl-5 text-lg md:pl-7",
        className,
      )}
    />
  );
}

export function ArticleOrderedListItem({
  className,
  ...props
}: HTMLAttributes<HTMLLIElement>) {
  return (
    <li
      {...props}
      className={cn("marker:text-primary pl-2 marker:font-bold", className)}
    />
  );
}

export function ArticleAlert({
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof Alert>, "variant">) {
  return (
    <Alert
      {...props}
      variant="info"
      className={cn("text-base [&>div]:text-base [&>h5]:text-base", className)}
    />
  );
}

export function ArticleAside({
  className,
  label,
  children,
  ...props
}: HTMLAttributes<HTMLElement> & {
  label?: React.ReactNode;
}) {
  return (
    <aside
      {...props}
      className={cn(
        "not-prose bg-muted rounded-xl border p-3 md:px-5",
        className,
      )}
    >
      {label ? (
        <div className="text-muted-foreground mb-2 text-sm font-medium">
          {label}
        </div>
      ) : null}
      <div className="space-y-2 text-base">{children}</div>
    </aside>
  );
}

export type ArticleHighlightImageProps =
  React.ImgHTMLAttributes<HTMLImageElement> & {
    alt: string;
    width?: number;
    height?: number;
  };

export function ArticleHighlightImage({
  alt,
  className,
  width = 808,
  height = 360,
  ...props
}: ArticleHighlightImageProps) {
  return (
    <div className="bg-muted -mx-[20px] mb-12 overflow-hidden sm:rounded-xl">
      <img
        height={height}
        width={width}
        {...props}
        alt={alt}
        className={cn("w-full", className)}
      />
    </div>
  );
}

type ArticleRelatedLinkPropsFor<TProps> = TProps extends unknown
  ? Omit<TProps, "children" | "className"> & {
      title: React.ReactNode;
      description?: React.ReactNode;
      label?: React.ReactNode;
      className?: string;
    }
  : never;

export type ArticleRelatedLinkProps = ArticleRelatedLinkPropsFor<CardLinkProps>;

export function ArticleRelatedLink({
  className,
  description,
  href,
  label = "Learn more",
  title,
  ...props
}: ArticleRelatedLinkProps) {
  return (
    <CardLink
      {...props}
      href={href}
      className={cn("not-prose hover:bg-muted/40", className)}
      srLabel="Learn more"
    >
      <div className="space-y-1 p-4 md:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="text-muted-foreground text-sm font-medium">
            {label}
          </div>
          <ArrowRightIcon
            className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </div>
        <div className="text-base leading-snug font-semibold group-hover:underline">
          {title}
        </div>
        {description ? (
          <div className="text-muted-foreground text-sm">{description}</div>
        ) : null}
      </div>
    </CardLink>
  );
}
